// preSave aggregated into Save All (spec/dashboard.md section 10.6): every extension runs, a block names its entities
// (every changed entity when it names none), a throw or a timeout blocks every changed entity, `Log.warn` becomes a warning.
import type { Document } from '../api/types';
import type { PreSaveBlock, PreSaveHook } from '../workspace/save';
import type { EntityKey } from '../workspace/Workspace';
import { changedEntitiesOf, type ExtensionHost } from './host';
import { unnyIdOf } from './model';
import { PRESAVE_TIMEOUT_MS } from './protocol';

export function extensionPreSaveHook(host: ExtensionHost): PreSaveHook {
  return async ({ drafts, workspace }) => {
    const ctx = changedEntitiesOf(workspace);
    host.lastPreSaveCtx = ctx;
    if (ctx.changedEntities.length === 0 || host.state.kind !== 'ready' || host.extensions.length === 0) return [];
    const outcomes = await host.preSave(ctx);
    const keyOf = new Map<string, EntityKey>();
    for (const d of drafts) {
      if (d.kind !== 'document' || d.actor !== 'user') continue;
      const doc = d.current as unknown as Document;
      keyOf.set(doc.id, d.key);
      keyOf.set(unnyIdOf(doc), d.key);
    }
    const allKeys = Array.from(new Set(keyOf.values()));
    const out: PreSaveBlock[] = [];
    for (const o of outcomes) {
      const named = o.result.kind === 'block' ? o.result.entities.map((e) => keyOf.get(e)).filter((k): k is EntityKey => !!k) : [];
      const targets = named.length > 0 ? named : allKeys;
      if (o.result.kind === 'block') for (const key of targets) out.push({ key, extension: o.extension, reason: o.result.reason });
      // Fail-closed (spec/dashboard.md section 10.6, the server's rule): a throw or a timeout blocks every changed entity.
      if (o.result.kind === 'failed') for (const key of allKeys) out.push({ key, extension: o.extension, reason: `Validator ${o.extension} failed: ${o.result.error}` });
      if (o.result.kind === 'timeout') for (const key of allKeys) out.push({ key, extension: o.extension, reason: `Validator ${o.extension} timed out (${Math.round(PRESAVE_TIMEOUT_MS / 1000)} s)` });
      for (const w of o.warnings) for (const key of targets) out.push({ key, extension: o.extension, reason: w, warning: true });
    }
    return out;
  };
}
